import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { OptimizerCandidate } from "../../services/optimizerService";
import { useBacktestStore } from "../../stores/backtestStore";
import MetricDelta from "./MetricDelta";
import OverfitBadge from "./OverfitBadge";

interface CandidateCardProps {
  candidate: OptimizerCandidate;
  rank: number;
  sourceMetrics?: Record<string, number>;
}

export default function CandidateCard({
  candidate,
  rank,
  sourceMetrics,
}: CandidateCardProps) {
  const navigate = useNavigate();
  const setStrategyCode = useBacktestStore((s) => s.setStrategyCode);
  const [showCode, setShowCode] = useState(false);

  const isBest = rank === 1;

  // Resolve overfit ratio from top-level or legacy walk_forward
  const overfitRatio =
    candidate.overfit_ratio ?? candidate.walk_forward?.overfit_ratio;
  // Resolve OOS metrics from top-level or legacy walk_forward
  const oosCagr =
    candidate.oos_metrics?.cagr ?? candidate.walk_forward?.oos_cagr;
  const oosSharpe =
    candidate.oos_metrics?.sharpe ?? candidate.walk_forward?.oos_sharpe;
  const oosMaxDD = candidate.oos_metrics?.max_drawdown;
  const hasOos = oosCagr !== undefined || oosSharpe !== undefined;

  const handleRunBacktest = () => {
    setStrategyCode(candidate.dsl_code);
    navigate("/backtest");
  };

  return (
    <div
      className={`rounded-xl border p-5 ${
        isBest
          ? "border-primary-500/40 bg-primary-500/5"
          : "border-surface-700/50 bg-surface-800/30"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-bold ${isBest ? "text-primary-400" : "text-surface-300"}`}
          >
            #{rank}
          </span>
          {isBest && (
            <span className="text-xs font-medium text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full border border-primary-500/20">
              ★ Best
            </span>
          )}
        </div>
        {overfitRatio !== undefined && (
          <OverfitBadge ratio={overfitRatio} />
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-4 gap-3 p-4 rounded-xl bg-surface-950/50 backdrop-blur-sm border border-surface-700/30 mb-4">
        <MetricDelta
          label="CAGR"
          current={candidate.metrics.cagr}
          original={sourceMetrics?.cagr}
          format="percent"
          higherIsBetter
        />
        <MetricDelta
          label="Sharpe"
          current={candidate.metrics.sharpe}
          original={sourceMetrics?.sharpe}
          format="ratio"
          higherIsBetter
        />
        <MetricDelta
          label="Max DD"
          current={candidate.metrics.max_drawdown}
          original={sourceMetrics?.max_drawdown}
          format="percent"
          higherIsBetter={false}
        />
        {overfitRatio !== undefined && (
          <div className="text-center">
            <div
              className={`text-lg font-bold ${
                overfitRatio > 0.7
                  ? "text-green-400"
                  : overfitRatio >= 0.5
                    ? "text-amber-400"
                    : "text-red-400"
              }`}
            >
              {overfitRatio.toFixed(2)}
            </div>
            <div className="text-xs text-surface-500 font-medium">Overfit</div>
          </div>
        )}
      </div>

      {/* IS vs OOS Comparison */}
      {hasOos && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* In-Sample */}
          <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/30">
            <div className="text-xs font-semibold text-surface-400 mb-2 uppercase tracking-wider">
              In-Sample (IS)
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-500">CAGR</span>
                <span className="text-surface-200 font-medium">
                  {candidate.metrics.cagr.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-500">Sharpe</span>
                <span className="text-surface-200 font-medium">
                  {candidate.metrics.sharpe.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-500">Max DD</span>
                <span className="text-surface-200 font-medium">
                  {candidate.metrics.max_drawdown.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
          {/* Out-of-Sample */}
          <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/30">
            <div className="text-xs font-semibold text-surface-400 mb-2 uppercase tracking-wider">
              Out-of-Sample (OOS)
            </div>
            <div className="space-y-1 text-sm">
              {oosCagr !== undefined && (
                <div className="flex justify-between">
                  <span className="text-surface-500">CAGR</span>
                  <span
                    className={`font-medium ${oosCagr >= candidate.metrics.cagr * 0.7 ? "text-green-400" : "text-red-400"}`}
                  >
                    {oosCagr.toFixed(1)}%
                  </span>
                </div>
              )}
              {oosSharpe !== undefined && (
                <div className="flex justify-between">
                  <span className="text-surface-500">Sharpe</span>
                  <span
                    className={`font-medium ${oosSharpe >= candidate.metrics.sharpe * 0.7 ? "text-green-400" : "text-red-400"}`}
                  >
                    {oosSharpe.toFixed(2)}
                  </span>
                </div>
              )}
              {oosMaxDD !== undefined && (
                <div className="flex justify-between">
                  <span className="text-surface-500">Max DD</span>
                  <span className="text-surface-200 font-medium">
                    {oosMaxDD.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleRunBacktest}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Run Full Backtest
        </button>

        <button
          onClick={() => setShowCode(!showCode)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-surface-400 hover:text-surface-200"
        >
          {showCode ? "Hide Code ▾" : "View Code ▸"}
        </button>
      </div>

      {/* Expandable Code Block */}
      {showCode && (
        <div className="mt-4 relative">
          <pre className="p-4 rounded-lg bg-surface-950 border border-surface-700/50 text-sm text-surface-300 overflow-x-auto font-mono">
            {candidate.dsl_code}
          </pre>
          <button
            onClick={() => navigator.clipboard.writeText(candidate.dsl_code)}
            className="absolute top-2 right-2 p-1.5 rounded bg-surface-800 text-surface-400 hover:text-surface-200 text-xs"
            title="Copy code"
          >
            Copy
          </button>
        </div>
      )}
    </div>
  );
}
